import { parseCookieValue, PARTNER } from "@tm/isomorphic-utils";

/** myaccount COUNTRY_INFO_MAPPING region values (lowercase). */
const CCDE_TO_REGION: Record<string, string> = {
  IN: "in",
  US: "us",
  AE: "ae",
  SA: "sa",
  OM: "om",
  KW: "kw",
};

const DEFAULT_REGION = "in";
const DEFAULT_ENTITY_NAME = "india";

export type PartnerIdentityHeaders = {
  region: string;
  "entity-name": string;
  brand: string;
  profileType: string;
};

type IdentityInput = {
  cookie?: string | string[] | null;
  partner?: string | null;
  /** URL query — only `region` is read when present. */
  query?: Record<string, string | string[] | undefined> | null;
};

function normalizeCookie(cookie?: string | string[] | null): string {
  if (!cookie) return "";
  return Array.isArray(cookie) ? cookie.join("; ") : cookie;
}

function pickQueryString(
  query: IdentityInput["query"],
  key: string
): string | undefined {
  if (!query) return undefined;
  const v = query[key];
  if (typeof v === "string" && v.trim()) return v.trim();
  if (Array.isArray(v) && typeof v[0] === "string" && v[0].trim()) {
    return v[0].trim();
  }
  return undefined;
}

function resolveRegion(
  cookieStr: string,
  query: IdentityInput["query"]
): string {
  const fromQuery = pickQueryString(query, "region");
  if (fromQuery) {
    return fromQuery.toLowerCase();
  }
  const ccde = (parseCookieValue(cookieStr, "ccde") || "").toUpperCase();
  return CCDE_TO_REGION[ccde] || DEFAULT_REGION;
}

function resolveProfileType(cookieStr: string): string {
  const subscription = (
    parseCookieValue(cookieStr, "myBusinessSubscription") || ""
  ).toLowerCase();
  if (subscription === "b2b") {
    return "CORPORATE";
  }
  if (subscription === "mypartner") {
    return "MYPARTNER";
  }
  // myaccount / Insurance redirect heuristic: corp auth token ends with "C"
  const mmtAuth = parseCookieValue(cookieStr, "mmt-auth") || "";
  if (mmtAuth && mmtAuth.charAt(mmtAuth.length - 1) === "C") {
    return "CORPORATE";
  }
  return "PERSONAL";
}

function resolveBrand(partner?: string | null): string {
  return partner === PARTNER.GI ? PARTNER.GI : PARTNER.MMT;
}

/**
 * Partner-identity headers for claims landing → CB.
 * Cookie/query sourced (myaccount-style); India-first defaults.
 */
export function getPartnerIdentityHeadersForClaims(
  input: IdentityInput = {}
): PartnerIdentityHeaders {
  const cookieStr = normalizeCookie(input.cookie);
  return {
    region: resolveRegion(cookieStr, input.query),
    "entity-name": DEFAULT_ENTITY_NAME,
    brand: resolveBrand(input.partner),
    profileType: resolveProfileType(cookieStr),
  };
}

/** Keys enhanceRequestHeaders must pass through when a caller sets them. */
export const PARTNER_IDENTITY_HEADER_KEYS = [
  "region",
  "entity-name",
  "brand",
  "profileType",
] as const;

export function pickPartnerIdentityHeaders(
  headers?: Record<string, unknown> | null
): Partial<PartnerIdentityHeaders> {
  if (!headers) return {};
  const out: Partial<PartnerIdentityHeaders> = {};
  for (const key of PARTNER_IDENTITY_HEADER_KEYS) {
    const value = headers[key];
    if (typeof value === "string" && value !== "") {
      out[key] = value;
    }
  }
  return out;
}
